import { message } from "antd";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { patchOrganization, useGetOrganizationById } from "../../../api/v1";
import { SelectOption } from "../../../libs/types";
import { findSelectOption } from "../../../libs/utils";

import {
  emptyFormValues,
  getCatalogueDef,
  getItemLabel,
  setValueByPath,
  stripUid,
  toFormValues,
  toPayload,
} from "./marketingConfigurationHelpers";
import { CatalogueId, MarketingConfigurationFormData, MarketingConfigurationItem } from "./types";

type UseMarketingConfigurationEditorOptions = {
  orgId: string | null;
  organizationOptions: SelectOption[];
  catalogueId: CatalogueId;
  items: MarketingConfigurationItem[];
  refetch: () => Promise<void>;
};

export function useMarketingConfigurationEditor(options: UseMarketingConfigurationEditorOptions) {
  const { orgId, organizationOptions, catalogueId, items, refetch } = options;

  const [modalMode, setModalMode] = useState<null | "add" | "edit">(null);
  const [editingRecord, setEditingRecord] = useState<MarketingConfigurationItem | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const methods = useForm<MarketingConfigurationFormData>({
    defaultValues: emptyFormValues,
  });

  const shouldFetchPublicListFlag = Boolean(modalMode) && catalogueId === "viewingDaysConfig";

  const { organization: moduleConfigOrg, isLoadingOrganization: isLoadingPublicListFlag } = useGetOrganizationById({
    orgId,
    params: { retrieveModuleConfigurations: true },
    isEnabled: shouldFetchPublicListFlag,
  });

  const enableMarketingPublicList = useMemo(
    () => Boolean(moduleConfigOrg?.enableMarketingPublicList),
    [moduleConfigOrg]
  );

  const resetForm = useCallback(() => {
    setModalMode(null);
    setEditingRecord(null);
    methods.reset(emptyFormValues);
  }, [methods]);

  const handleAdd = useCallback(() => {
    if (!orgId) {
      message.warning("Select an organization first");
      return;
    }

    setEditingRecord(null);
    setModalMode("add");
    methods.reset({
      ...emptyFormValues,
      organization: findSelectOption(organizationOptions, orgId) ?? null,
    });
  }, [methods, orgId, organizationOptions]);

  const handleEdit = useCallback(
    (record: MarketingConfigurationItem) => {
      setEditingRecord(record);
      setModalMode("edit");
      methods.reset({
        ...emptyFormValues,
        organization: orgId ? (findSelectOption(organizationOptions, orgId) ?? null) : null,
        ...toFormValues(record),
      });
    },
    [methods, orgId, organizationOptions]
  );

  const handleSavePublicListUrl = useCallback(
    async (url: string) => {
      if (!orgId) {
        message.warning("Select an organization first");
        return false;
      }

      const trimmedUrl = url.trim();
      if (!trimmedUrl) {
        message.warning("Enter a public list URL");
        return false;
      }

      setIsSaving(true);

      try {
        const response = await patchOrganization({
          orgId,
          data: {
            configData: {
              marketingPublicListUrl: trimmedUrl,
            },
          },
        });

        if (response?.success) {
          message.success("Public list URL saved");
          await refetch();
          return true;
        }

        message.error(response?.message ?? "Unable to save");
        return false;
      } catch {
        message.error("Something went wrong");
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [orgId, refetch]
  );

  const handleSubmit = useCallback(
    async (formData: MarketingConfigurationFormData) => {
      if (!orgId) {
        message.warning("Select an organization");
        return;
      }

      const payload = toPayload(catalogueId, formData);
      const nextItem = {
        uid: editingRecord?.uid ?? crypto.randomUUID(),
        ...payload,
      } as MarketingConfigurationItem;
      const label = getItemLabel(nextItem);

      const updatedList =
        modalMode === "edit" && editingRecord
          ? items.map((item) => (item.uid === editingRecord.uid ? nextItem : item))
          : [...items, nextItem];

      if (
        catalogueId === "viewingDaysConfig" &&
        enableMarketingPublicList &&
        !updatedList.some((item) => item.isActive)
      ) {
        message.error("At least one viewing day must be active when marketing public list is enabled");
        return;
      }

      setIsSaving(true);

      try {
        const { filterKey } = getCatalogueDef(catalogueId);
        const response = await patchOrganization({
          orgId,
          data: setValueByPath(filterKey, updatedList.map(stripUid)),
        });

        if (response?.success) {
          message.success(modalMode === "edit" ? `${label} updated` : `${label} added`);
          resetForm();
          await refetch();
        } else {
          message.error(response?.message ?? "Unable to update");
        }
      } catch {
        message.error("Something went wrong");
      } finally {
        setIsSaving(false);
      }
    },
    [catalogueId, editingRecord, enableMarketingPublicList, items, modalMode, orgId, refetch, resetForm]
  );

  return {
    modalMode,
    editingRecord,
    methods,
    isSaving,
    enableMarketingPublicList,
    isLoadingPublicListFlag: shouldFetchPublicListFlag && isLoadingPublicListFlag,
    handleAdd,
    handleEdit,
    handleSavePublicListUrl,
    handleSubmit,
    resetForm,
  };
}
